// src/theme.js — Design tokens projetés sur les CSS variables (cf. index.css).
// Light/dark switch se fait au niveau CSS via `<html data-theme="dark">`.
// Les composants qui consomment `colors.xxx` reçoivent une string `var(--xxx)`
// que CSS résout en fonction du thème courant. Pas besoin de re-render React :
// les valeurs CSS sont évaluées à chaque paint.
//
// IMPORTANT : ne JAMAIS faire `${colors.accent}10` ou autre concaténation —
// utiliser les variables `bgAccent`, `accentSoft` ou rgb(var(--accent-rgb)).

const v = (name) => `var(--${name})`

export const colors = {
  // Backgrounds
  bgPage:       v('bg-page'),
  bgCard:       v('bg-card'),
  bgPanel:      v('bg-panel'),
  bgAccent:     v('bg-accent'),
  bgHover:      v('bg-hover'),

  // Text
  textPrimary:   v('text-primary'),
  textSecondary: v('text-secondary'),
  textMuted:     v('text-muted'),
  textFaint:     v('text-faint'),

  // Borders
  border:        v('line'),
  borderStrong:  v('line-strong'),
  borderHover:   v('line-hover'),

  // Brand bordeaux
  accent:        v('accent'),
  accentDeep:    v('accent-deep'),
  accentSoft:    v('accent-soft'),
  accentInk:     v('accent-ink'),
  // Alias historique : plusieurs écrans consomment `accentBg` (aplat bordeaux
  // très clair). Le token réel est `--bg-accent`. On expose l'alias pour éviter
  // un `undefined` silencieux (fond transparent) dans LawyerMessage, l'historique
  // agent, TeamPage, etc.
  accentBg:      v('bg-accent'),

  // Statuts
  statusOk:    v('status-ok'),
  statusWarn:  v('status-warn'),
  statusAlert: v('status-alert'),
  statusMiss:  v('status-miss'),

  // Variantes douces (fonds/bordures translucides) — déjà définies dans index.css.
  // À utiliser au lieu de concaténer une alpha (`${accent}10` produit du CSS invalide).
  statusOkSoft:   v('status-ok-soft'),
  statusOkLine:   v('status-ok-line'),
  statusWarnSoft: v('status-warn-soft'),
  statusMissSoft: v('status-miss-soft'),
  statusMissLine: v('status-miss-line'),

  // Info (bleu) — pour les états neutres/informatifs
  info:     v('info'),
  infoSoft: v('info-soft'),
  infoLine: v('info-line'),

  // Encre — sidebar "Premium contrasté" (reste sombre dans les deux thèmes)
  ink:      v('ink'),
  ink2:     v('ink-2'),
  inkLine:  v('ink-line'),
  inkText:  v('ink-text'),
  inkMuted: v('ink-muted'),
  inkFaint: v('ink-faint'),

  // Ombres
  shadowSm: v('shadow-sm'),
  shadowMd: v('shadow-md'),
  shadowAccent: v('shadow-accent'),

  // Radius — échelle E (cf. --radius-* dans index.css)
  radiusCard:  v('radius-card'),   // 18px — cartes
  radiusSoft:  v('radius-soft'),   // 24px — grandes surfaces douces
  radiusInput: v('radius-input'),  // 14px — inputs / champs

  // Overlay (backdrop des modales)
  overlay: v('overlay'),
}

export const fonts = {
  sans:  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  serif: "'Spectral', Georgia, 'Times New Roman', serif",
  mono:  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",

  // Socle « Le Cabinet » — fonts de la maquette le-cabinet.
  // NON-CASSANT : `serif`/`mono` ci-dessus restent E (Spectral/JetBrains) pour
  // les écrans non basculés. Ces alias servent les écrans « Le Cabinet ».
  serifDisplay: "'Fraunces', Georgia, 'Times New Roman', serif",
  monoCab:      "'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace",
}

// === Socle « Le Cabinet » =================================================
// Tokens projetés sur les CSS variables `--cab-*` (cf. index.css).
// Même pattern que `colors` : chaque valeur est une string `var(--cab-…)`.
// NON-CASSANT : objet additionnel — n'altère ni `colors` ni `theme`.
export const cab = {
  // Substrat clair, chaud
  paper:     v('cab-paper'),
  paper2:    v('cab-paper-2'),
  card:      v('cab-card'),
  cardSoft:  v('cab-card-soft'),
  hair:      v('cab-hair'),
  hair2:     v('cab-hair-2'),
  hair3:     v('cab-hair-3'),

  // Encre chaude
  ink:   v('cab-ink'),
  ink2:  v('cab-ink-2'),
  ink3:  v('cab-ink-3'),
  ink4:  v('cab-ink-4'),

  // Bordeaux, l'ADN
  bordeaux:     v('cab-bordeaux'),
  bordeaux2:    v('cab-bordeaux-2'),
  bordeauxDeep: v('cab-bordeaux-deep'),
  bordeauxHi:   v('cab-bordeaux-hi'),
  bordeauxTint:  v('cab-bordeaux-tint'),
  bordeauxTint2: v('cab-bordeaux-tint-2'),
  bordeauxGlow:  v('cab-bordeaux-glow'),

  // Or sobre (livrables)
  gild:    v('cab-gild'),
  gild2:   v('cab-gild-2'),
  gildHi:  v('cab-gild-hi'),
  gildTint:v('cab-gild-tint'),

  verified: v('cab-verified'),

  // Radius « objet posé »
  rXl: v('cab-r-xl'),
  rLg: v('cab-r-lg'),
  rMd: v('cab-r-md'),
  rSm: v('cab-r-sm'),

  // Ombres « objet posé »
  lift1: v('cab-lift-1'),
  lift2: v('cab-lift-2'),
  lift3: v('cab-lift-3'),

  // Easings
  ease:     v('cab-ease'),
  easeSoft: v('cab-ease-soft'),
}

// Couleurs de matière du Cabinet (badge + accents). Clé = matière `client_dossiers`.
// Renvoie { color, soft } en `var(--cab-…)`. `soft` peut être absent (fallback hair).
export const CAB_MATIERE = {
  penal:       { color: v('cab-penal'),       soft: v('cab-penal-soft') },
  civil:       { color: v('cab-civil'),       soft: v('cab-civil-soft') },
  famille:     { color: v('cab-famille'),     soft: v('cab-famille-soft') },
  corporel:    { color: v('cab-corporel'),    soft: v('cab-corporel-soft') },
  secretariat: { color: v('cab-secretariat'), soft: v('cab-secretariat-soft') },
  immobilier:  { color: v('cab-immobilier'),  soft: v('cab-immobilier-soft') },
  pi:          { color: v('cab-pi'),          soft: v('cab-pi-soft') },
  consommation:{ color: v('cab-pi'),          soft: v('cab-pi-soft') },
  travail:     { color: v('cab-travail'),     soft: v('cab-travail-soft') },
  affaires:    { color: v('cab-affaires'),    soft: v('cab-civil-soft') },
  commercial:  { color: v('cab-affaires'),    soft: v('cab-civil-soft') },
  societes:    { color: v('cab-affaires'),    soft: v('cab-civil-soft') },
  'procedures-collectives': { color: v('cab-proc'), soft: v('cab-secretariat-soft') },
  fiscal:      { color: v('cab-fiscal'),      soft: v('cab-pi-soft') },
  rgpd:        { color: v('cab-rgpd'),        soft: v('cab-rgpd-soft') },
  environnement:{ color: v('cab-env'),        soft: v('cab-immobilier-soft') },
  administratif:{ color: v('cab-admin'),      soft: v('cab-admin-soft') },
  successions: { color: v('cab-penal'),       soft: v('cab-penal-soft') },
}

// Helper : matière → { label, color, soft }. Label humanisé. Fallback neutre.
const CAB_MATIERE_LABEL = {
  penal: 'Pénal', civil: 'Civil', famille: 'Famille', corporel: 'Corporel',
  secretariat: 'Secrétariat', immobilier: 'Immobilier', pi: 'PI & Conso',
  consommation: 'Consommation', travail: 'Social', affaires: 'Affaires',
  commercial: 'Commercial', societes: 'Sociétés',
  'procedures-collectives': 'Proc. coll.', fiscal: 'Fiscal', rgpd: 'RGPD',
  environnement: 'Environnement', administratif: 'Administratif',
  successions: 'Successions',
}
export function cabMatiere(matiere) {
  const key = String(matiere || '').toLowerCase().trim()
  const m = CAB_MATIERE[key]
  return {
    label: CAB_MATIERE_LABEL[key] || (matiere ? String(matiere) : 'Dossier'),
    color: m?.color || v('cab-ink-3'),
    soft:  m?.soft  || v('cab-hair'),
  }
}

// Backward-compat — la plupart des composants existants importent `theme` ou `T`.
// On garde les anciennes clés mappées sur les nouvelles valeurs pour éviter
// une cascade de remplacements.
export const theme = {
  ...colors,
  bg: colors.bgPage,
  cardBg: colors.bgCard,
  text: colors.textPrimary,
  textSecondary: colors.textSecondary,
  border: colors.border,
  hover: colors.bgHover,
  accent: colors.accent,
  green: colors.accent,
  greenBg: colors.bgAccent,
  serif: fonts.serif,
  sans: fonts.sans,
  mono: fonts.mono,
  sectionGap: 40,
  cardRadius: 18,
}

// Theme toggle helpers — utilisés depuis la sidebar.
export function getDarkMode() {
  if (typeof document === 'undefined') return false
  return document.documentElement.dataset.theme === 'dark'
}
export function setDarkMode(isDark) {
  if (typeof document === 'undefined') return
  if (isDark) {
    document.documentElement.setAttribute('data-theme', 'dark')
  } else {
    document.documentElement.removeAttribute('data-theme')
  }
  try { localStorage.setItem('iavocat-dark-mode', String(isDark)) } catch {}
}
// Read persisted preference on app startup.
export function initDarkModeFromStorage() {
  if (typeof document === 'undefined' || typeof localStorage === 'undefined') return false
  const saved = localStorage.getItem('iavocat-dark-mode')
  const isDark = saved === 'true'
  if (isDark) document.documentElement.dataset.theme = 'dark'
  return isDark
}
